import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import PageLayout from '../../components/layouts/PageLayout';
import { authService } from '../../auth/authService';
import { canAccessPage, isPeerReviewAnalyticsRadiologistScoped } from '../../utils/permissions';
import { getDefaultRoute } from '../../utils/getDefaultRoute';
import {
  getPeerReviewAnalyticsCases,
  PeerReviewAnalyticsCaseRow,
} from '../../services/peerReviewAnalyticsService';
import { drillMetricLabel, type PeerReviewDrillMetric } from '../../utils/peerReviewAnalyticsDrill';
import '../../components/analytics/analytics-theme.css';
import '../admin/ReportDeliveryAnalytics.css';
import './PeerReviewAnalytics.css';

function readSearchFilters(params: URLSearchParams) {
  return {
    startDate: params.get('start_date') || params.get('startDate') || '',
    endDate: params.get('end_date') || params.get('endDate') || '',
    modality: params.get('modality') || undefined,
    radiologistId: params.get('radiologist_id') || params.get('radiologistId') || undefined,
    remark: params.get('remark') || undefined,
    priority: params.get('priority') || undefined,
    bodyPart: params.get('body_part') || params.get('bodyPart') || undefined,
    trendPeriod: (params.get('trend_period') || params.get('trendPeriod') || undefined) as
      | 'day'
      | 'week'
      | 'month'
      | undefined,
    metric: (params.get('metric') || params.get('drill_metric') || undefined) as
      | PeerReviewDrillMetric
      | undefined,
    trendBucket: params.get('trend_bucket') || params.get('trendBucket') || undefined,
    drillModality: params.get('drill_modality') || params.get('drillModality') || undefined,
    drillReviewerId:
      params.get('drill_reviewer_id') ||
      params.get('drillReviewerId') ||
      params.get('drill_radiologist_id') ||
      undefined,
  };
}

function formatDateTime(value: string | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

function renderCasesTableBody(
  loading: boolean,
  rows: PeerReviewAnalyticsCaseRow[]
): React.ReactNode {
  if (loading) {
    return (
      <tr>
        <td colSpan={8}>Loading…</td>
      </tr>
    );
  }
  if (rows.length === 0) {
    return (
      <tr>
        <td colSpan={8}>No cases match the selected drill-down filters.</td>
      </tr>
    );
  }
  return rows.map(row => (
    <tr key={row.id}>
      <td>{row.peerReviewerName || '—'}</td>
      <td>{row.status}</td>
      <td>{row.remark || '—'}</td>
      <td>{row.modality || '—'}</td>
      <td>{row.bodyPart || '—'}</td>
      <td>{row.priority || '—'}</td>
      <td>{formatDateTime(row.createdAt)}</td>
      <td>{formatDateTime(row.submittedAt)}</td>
    </tr>
  ));
}

export default function PeerReviewAnalyticsCases() {
  const [searchParams] = useSearchParams();
  const filters = useMemo(() => readSearchFilters(searchParams), [searchParams]);
  const [rows, setRows] = useState<PeerReviewAnalyticsCaseRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const user = authService.getUser();
  const radiologistScoped = isPeerReviewAnalyticsRadiologistScoped(user);
  const homePath = getDefaultRoute(user);
  const hasAccess = canAccessPage(user, '/analytics/peer-review');
  const hasDateRange = Boolean(filters.startDate && filters.endDate);

  useEffect(() => {
    setPage(1);
  }, [
    filters.startDate,
    filters.endDate,
    filters.modality,
    filters.radiologistId,
    filters.remark,
    filters.priority,
    filters.bodyPart,
    filters.trendPeriod,
    filters.metric,
    filters.trendBucket,
    filters.drillModality,
    filters.drillReviewerId,
  ]);

  useEffect(() => {
    if (!hasAccess || !hasDateRange) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getPeerReviewAnalyticsCases({
      startDate: filters.startDate,
      endDate: filters.endDate,
      modality: filters.modality,
      radiologistId: radiologistScoped ? undefined : filters.radiologistId,
      remark: filters.remark,
      priority: filters.priority,
      bodyPart: filters.bodyPart,
      trendPeriod: filters.trendPeriod,
      metric: filters.metric,
      trendBucket: filters.trendBucket,
      drillModality: filters.drillModality,
      drillReviewerId: filters.drillReviewerId,
      page,
      limit: 25,
    })
      .then(res => {
        if (cancelled) {
          return;
        }
        if (res.success && res.data) {
          setRows(res.data.cases);
          setTotal(res.data.pagination.total);
        } else {
          setRows([]);
          setTotal(0);
          setError(res.error || 'Failed to load peer review cases');
        }
      })
      .catch(err => {
        if (cancelled) {
          return;
        }
        setRows([]);
        setTotal(0);
        setError(err instanceof Error ? err.message : 'Failed to load peer review cases');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filters, page, hasAccess, hasDateRange, radiologistScoped]);

  if (!hasAccess) {
    return (
      <Navigate
        to={homePath}
        replace
      />
    );
  }

  if (!hasDateRange) {
    return (
      <Navigate
        to="/analytics/peer-review"
        replace
      />
    );
  }

  const dashboardHref = `/analytics/peer-review?${new URLSearchParams({
    start_date: filters.startDate,
    end_date: filters.endDate,
    ...(filters.modality ? { modality: filters.modality } : {}),
    ...(!radiologistScoped && filters.radiologistId
      ? { radiologist_id: filters.radiologistId }
      : {}),
    ...(filters.remark ? { remark: filters.remark } : {}),
    ...(filters.priority ? { priority: filters.priority } : {}),
    ...(filters.bodyPart ? { body_part: filters.bodyPart } : {}),
    ...(filters.trendPeriod ? { trend_period: filters.trendPeriod } : {}),
  }).toString()}`;

  const totalPages = Math.max(1, Math.ceil(total / 25));

  const titleParts = [
    drillMetricLabel(filters.metric),
    filters.drillModality ? `· ${filters.drillModality}` : null,
    filters.trendBucket ? `· ${filters.trendBucket}` : null,
  ].filter(Boolean);

  return (
    <PageLayout
      title="Peer review cases"
      hideTitleInHeader
      contentClassName="rda-page-layout-content"
      breadcrumbs={[
        { label: 'Home', path: homePath },
        { label: 'Peer review', path: '/analytics/peer-review' },
        { label: 'Cases' },
      ]}
    >
      <div className="rda-dashboard">
        <header className="rda-dashboard-subheader pra-cases-header">
          <div>
            <h1 className="rda-dashboard-subheader-title">Peer review cases</h1>
            <p className="pra-cases-subtitle">
              {titleParts.join(' ')} · {filters.startDate} to {filters.endDate}
            </p>
          </div>
          <Link
            className="pra-back-link"
            to={dashboardHref}
          >
            Back to dashboard
          </Link>
        </header>

        <div className="rda-dashboard-container">
          {error && (
            <p
              className="rda-error"
              role="alert"
            >
              {error}
            </p>
          )}

          <section
            className="rda-table-section"
            aria-label="Filtered peer review cases"
          >
            <table className="rda-table">
              <thead>
                <tr>
                  <th scope="col">Reviewer</th>
                  <th scope="col">Status</th>
                  <th scope="col">Remark</th>
                  <th scope="col">Modality</th>
                  <th scope="col">Body part</th>
                  <th scope="col">Priority</th>
                  <th scope="col">Assigned</th>
                  <th scope="col">Submitted</th>
                </tr>
              </thead>
              <tbody>{renderCasesTableBody(loading, rows)}</tbody>
            </table>

            {!loading && totalPages > 1 && (
              <div className="pra-cases-pagination">
                <button
                  type="button"
                  className="pra-cases-pagination__btn"
                  disabled={page <= 1}
                  onClick={() => setPage(current => Math.max(1, current - 1))}
                >
                  Previous
                </button>
                <span className="pra-cases-pagination__label">
                  Page {page} of {totalPages} · {total} cases
                </span>
                <button
                  type="button"
                  className="pra-cases-pagination__btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage(current => Math.min(totalPages, current + 1))}
                >
                  Next
                </button>
              </div>
            )}
          </section>
        </div>
      </div>
    </PageLayout>
  );
}
